import { hydrateTemperatureTrends } from './temperature-trends.js';
import { hydrateThermalContext } from './station-thermal.js';
// Wiring scheda stazione v2: popup MapLibre su desktop, modale su mobile
// (breakpoint 820 come v1), azioni condividi/segnala. La scheda è costruita da
// station-card.js (port 1:1 dei valori v1).
import { buildStationCardHtml, hydrateStationCard } from './station-card.js';
import { isMobile } from '../lib/config.js';

let popup = null;
let popupMap = null;
let modal = null;
let activeStationId = '';

function currentParametro() {
    return document.getElementById('selectParametro')?.value || 'temperatura';
}

function esc(s) {
    return String(s ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}
function stationShortName(rec) {
    const s = rec.station || {};
    return s.localita || s.location || s.comune || s.city || s.name || s.nome || 'Stazione';
}
// Barra "più stazioni qui": compare SOLO quando il click cade su ≥2 stazioni
// impilate (es. Nicito/Ughetti sullo stesso pixel). Ogni chip cambia la scheda
// mostrata senza chiudere il popup → si raggiunge quella sotto senza zoomare.
function clusterBarHtml(cluster, activeId) {
    if (!cluster || cluster.length < 2) return '';
    const chips = cluster.map(r => `<button type="button" class="wsv2-cluster-chip${r.id === activeId ? ' is-active' : ''}" data-cluster-id="${esc(r.id)}">${esc(stationShortName(r))}</button>`).join('');
    return `<div class="wsv2-cluster-bar"><span class="wsv2-cluster-bar__lbl">${cluster.length} stazioni qui</span><div class="wsv2-cluster-bar__chips">${chips}</div></div>`;
}
function wireClusterChips(container, cluster, reopen) {
    container.querySelectorAll('[data-cluster-id]').forEach(btn => {
        btn.addEventListener('click', (ev) => {
            ev.stopPropagation();
            const id = btn.dataset.clusterId;
            const target = cluster.find(r => String(r.id) === String(id));
            if (target) reopen(target);
        });
    });
}

function wireActions(container, rec) {
    container.querySelector('[data-action="share"]')?.addEventListener('click', async (e) => {
        const url = e.currentTarget.dataset.shareUrl || window.location.href;
        try {
            if (navigator.share) await navigator.share({ title: rec.station.name || 'Stazione', url });
            else await navigator.clipboard.writeText(url);
        } catch (err) { /* annullato */ }
    });
    container.querySelector('[data-action="report-station"]')?.addEventListener('click', () => {
        openReportDialog(rec);
    });
}

function popupPlacement(map, rec) {
    const point = map.project([rec.lon, rec.lat]);
    const box = map.getContainer().getBoundingClientRect();
    const vertical = point.y >= box.height / 2 ? 'bottom' : 'top';
    const horizontal = point.x < 215 ? '-left' : (point.x > box.width - 215 ? '-right' : '');
    const available = vertical === 'bottom' ? point.y : box.height - point.y;
    return { anchor: vertical + horizontal, maxHeight: Math.max(220, Math.floor(available - 30)) };
}

function openDesktop(map, rec, parametro, cluster) {
    popup?.remove();
    activeStationId = String(rec.id);
    const placement = popupPlacement(map, rec);
    popup = new maplibregl.Popup({
        className: 'wsv2-station-popup', maxWidth: '390px', offset: 12,
        closeButton: true, closeOnClick: true, anchor: placement.anchor,
    }).setLngLat([rec.lon, rec.lat]).setHTML(clusterBarHtml(cluster, rec.id) + buildStationCardHtml(rec, parametro, popupMap)).addTo(map);
    const el = popup.getElement();
    const content = el.querySelector('.maplibregl-popup-content');
    if (content) content.style.maxHeight = `${placement.maxHeight}px`;
    hydrateStationCard(el, rec, parametro);
    wireActions(el, rec);
    wireClusterChips(el, cluster, (target) => openDesktop(map, target, parametro, cluster));
    popup.on('close', () => {
        if (activeStationId === String(rec.id)) activeStationId = '';
    });
}

function openMobile(rec, parametro, cluster) {
    closeModal();
    activeStationId = String(rec.id);
    modal = document.createElement('div');
    modal.className = 'ws-station-modal';
    // TUTTA la scheda scorre come un unico blocco (niente titolo fisso: con la
    // barra fissa il contenuto le passava sopra). La ✕ è un pulsantino flottante
    // sull'angolo, sempre raggiungibile.
    modal.innerHTML = `
        <div class="ws-station-modal__scrim" data-close></div>
        <div class="ws-station-modal__sheet">
            <button class="ws-station-modal__close" data-close aria-label="Chiudi">✕</button>
            <div class="ws-station-modal__body">${clusterBarHtml(cluster, rec.id)}${buildStationCardHtml(rec, parametro, popupMap)}</div>
        </div>`;
    document.body.appendChild(modal);
    modal.querySelectorAll('[data-close]').forEach(n => n.addEventListener('click', closeModal));
    hydrateStationCard(modal, rec, parametro);
    wireActions(modal, rec);
    wireClusterChips(modal, cluster, (target) => openMobile(target, parametro, cluster));
}

function closeModal() {
    modal?.remove();
    modal = null;
    activeStationId = '';
}

// Il logo e il relativo contenitore sono statici: mantenerli nel DOM evita il
// lampeggio dell'immagine a ogni campione Instant. Nell'header cambiano solo il
// blocco descrittivo (badge/stato/nome) e l'orario dell'ultima osservazione.
function refreshStationHeader(currentHead, nextHead) {
    const currentMain = currentHead.firstElementChild;
    const nextMain = nextHead.firstElementChild;
    if (currentMain && nextMain) currentMain.replaceWith(nextMain);

    const currentMeta = currentHead.querySelector('.ws-station-card__head-meta');
    const nextMeta = nextHead.querySelector('.ws-station-card__head-meta');
    if (!currentMeta || !nextMeta) {
        if (currentMeta) currentMeta.remove();
        if (nextMeta) currentHead.appendChild(nextMeta);
        return;
    }

    const currentLogo = currentMeta.querySelector('.ws-station-card__network-logo');
    const nextLogo = nextMeta.querySelector('.ws-station-card__network-logo');
    const sameLogo = (!currentLogo && !nextLogo)
        || (currentLogo && nextLogo && currentLogo.getAttribute('src') === nextLogo.getAttribute('src'));
    if (!sameLogo) {
        currentMeta.replaceWith(nextMeta);
        return;
    }

    const currentTime = currentMeta.querySelector('.ws-station-card__time');
    const nextTime = nextMeta.querySelector('.ws-station-card__time');
    if (currentTime && nextTime) currentTime.textContent = nextTime.textContent;
    else if (currentTime) currentTime.remove();
    else if (nextTime) currentMeta.appendChild(nextTime);
}

// Aggiorna soltanto le parti live della scheda, senza chiuderla, perdere lo
// scroll o ricreare grafici/annunci. Il record usato al click non resta quindi
// congelato mentre marker e dataset avanzano.
function refreshOpenStation(records, stations, parametro) {
    if (!activeStationId) return;
    const rec = (Array.isArray(records) ? records : [])
        .find(item => String(item.id) === activeStationId)
        || (Array.isArray(stations) ? stations : [])
            .find(item => String(item.id) === activeStationId);
    if (!rec) return;

    const root = modal || popup?.getElement();
    const currentCard = root?.querySelector('.ws-station-card');
    if (!currentCard) return;
    void hydrateThermalContext(root, rec);

    const holder = document.createElement('div');
    holder.innerHTML = buildStationCardHtml(rec, parametro, popupMap);
    const nextCard = holder.querySelector('.ws-station-card');
    if (!nextCard) return;

    const currentHead = currentCard.querySelector('.ws-station-card__head');
    const nextHead = nextCard.querySelector('.ws-station-card__head');
    if (currentHead && nextHead) refreshStationHeader(currentHead, nextHead);

    const currentHero = currentCard.querySelector('.ws-station-card__hero');
    const nextHero = nextCard.querySelector('.ws-station-card__hero');
    if (currentHero && nextHero) currentHero.replaceWith(nextHero);
    void hydrateTemperatureTrends(root, rec);
    const currentSections = currentCard.querySelectorAll('.ws-station-card__section');
    const nextSections = nextCard.querySelectorAll('.ws-station-card__section');
    currentSections.forEach((section, index) => {
        if (nextSections[index]) section.replaceWith(nextSections[index]);
    });
}

function openReportDialog(rec) {
    const dlg = document.createElement('div');
    dlg.className = 'ws-station-modal';
    const name = rec.station.name || rec.station.nome || 'Stazione';
    dlg.innerHTML = `
        <div class="ws-station-modal__scrim" data-close></div>
        <div class="ws-station-modal__sheet wsv2-report">
            <button class="ws-station-modal__close" data-close aria-label="Chiudi">✕</button>
            <h3>Segnala anomalia · ${name}</h3>
            <p>Descrivi cosa non torna (dato sospetto, stazione ferma…).</p>
            <textarea maxlength="1200" rows="4" placeholder="Il dato di … sembra…"></textarea>
            <input type="email" placeholder="Email (facoltativa)">
            <input type="text" name="website" class="wsv2-hp" tabindex="-1" autocomplete="off" aria-hidden="true">
            <button class="wsv2-report-send">Invia segnalazione</button>
            <div class="wsv2-report-status" role="status"></div>
        </div>`;
    document.body.appendChild(dlg);
    dlg.querySelectorAll('[data-close]').forEach(n => n.addEventListener('click', () => dlg.remove()));
    dlg.querySelector('.wsv2-report-send').addEventListener('click', async () => {
        const status = dlg.querySelector('.wsv2-report-status');
        const message = dlg.querySelector('textarea').value.trim();
        if (!message) { status.textContent = 'Scrivi due righe sul problema.'; return; }
        status.textContent = 'Invio…';
        try {
            // Intelligence exposes a cookie/Bearer + same-origin JSON write
            // contract; ordinary Maps keeps its existing public-domain route.
            const reportEndpoint = document.documentElement.classList.contains('wsi-product')
                ? '/api/station-report-v2.php'
                : '/api/station-report.php';
            const resp = await fetch(reportEndpoint, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    message,
                    email: dlg.querySelector('input[type=email]').value.trim(),
                    website: dlg.querySelector('.wsv2-hp').value, // honeypot
                    station_id: rec.id,
                    station_name: name,
                }),
            });
            const data = await resp.json().catch(() => ({}));
            status.textContent = resp.ok ? 'Grazie! Segnalazione ricevuta.' : (data.error || 'Invio non riuscito, riprova.');
            if (resp.ok) setTimeout(() => dlg.remove(), 1600);
        } catch (e) { status.textContent = 'Invio non riuscito, riprova.'; }
    });
}

export function initStationPopup(map) {
    popupMap = map;
    document.addEventListener('wsv2:station-click', (e) => {
        const rec = e.detail.rec;
        const cluster = e.detail.cluster || [rec];
        const parametro = currentParametro();
        if (isMobile()) openMobile(rec, parametro, cluster);
        else openDesktop(map, rec, parametro, cluster);
    });
    document.addEventListener('wsv2:stations', (e) => {
        refreshOpenStation(
            e.detail?.records,
            e.detail?.stations,
            e.detail?.parametro || currentParametro(),
        );
    });
}
